import * as Immutable from 'seamless-immutable';
import { Action } from 'redux';
import {
  updateFeaturedClusters,
  addFeaturedCluster,
  removeFeaturedCluster,
  removeUnsavedFeaturedClusters,
} from './featured-cluster.actions';
import { isType } from 'typescript-fsa/lib';
import { AppState } from '../../reducers';
import * as R from 'ramda';
import { Cluster } from '../Clusters/clusters.reducer';

export interface TranslatableCluster {
  id: number;
  name: string;
  headline: {
    [key: string]: string;
  };
  subline: {
    [key: string]: string;
  };
  text: {
    [key: string]: string;
  };
}

export interface FeaturedCluster {
  gender: string;
  appId: number;
  id: number;
  clusterId: number;
  position: number;
  activeFrom: string | null;
  activeTo: string | null;
  cluster?: Cluster;
  dirty?: boolean;
}

export interface FeaturedClusters {
  byId: {
    [key: number]: FeaturedCluster;
  };
}

const initialState = Immutable.from<FeaturedClusters>({ byId: {} });
export type FeaturedClusterState = FeaturedClusters &
  Immutable.ImmutableObject<FeaturedClusters>;

export default function reduce(
  state: FeaturedClusterState = initialState,
  action: Action
) {
  if (isType(action, updateFeaturedClusters)) {
    const { payload } = action;
    return state.merge(
      {
        byId: payload,
      },
      { deep: true }
    );
  }

  if (isType(action, addFeaturedCluster)) {
    const { payload } = action;

    return state.merge(
      {
        byId: {
          [payload.id]: {
            dirty: true,
            ...payload
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, removeUnsavedFeaturedClusters)) {
    const { byId } = state;

    return state.merge({
      byId: R.pickBy((val, key) => (key as any) > 0, byId),
    });
  }

  if (isType(action, removeFeaturedCluster)) {
    const { payload } = action;
    const { byId } = state;

    return state.merge({
      byId: {
        ...R.filter((cluster: FeaturedCluster) => {
          return cluster.id !== payload.id;
        }, byId),
      },
    });
  }

  return state;
}

export function getFeaturedClusters(state: AppState): FeaturedCluster[] {
  const {
    entities: { featuredClusters },
  } = state;
  if (!featuredClusters || !featuredClusters.byId) {
    return [];
  }

  return R.values(featuredClusters.byId);
}

export function getDirtyFeaturedClusters(state: AppState): FeaturedCluster[] {
  return R.filter(
    (item: FeaturedCluster) => item.dirty === true,
    getFeaturedClusters(state)
  );
}

export function getFeaturedClustersByClusterId(
  state: AppState,
  clusterId: number
): FeaturedCluster[] {
  const {
    entities: { featuredClusters },
  } = state;

  if (!featuredClusters || !featuredClusters.byId) {
    return [];
  }

  return R.values(
    R.pickBy((val, key) => val.clusterId === clusterId, featuredClusters.byId)
  );
}

export function getUnsavedFeaturedClusters(state: AppState): FeaturedCluster[] {
  return R.filter(
    (item: FeaturedCluster) => item.id < 0,
    getFeaturedClusters(state)
  );
}

export function getFeaturedClustersByGenderAndAppId(
  state: AppState,
  gender: string,
  appId: number
): FeaturedCluster[] {
  const {
    entities: { featuredClusters },
  } = state;
  if (!featuredClusters) {
    return [];
  }

  return R.filter(
    (fCluster: FeaturedCluster) =>
      fCluster.gender === gender && fCluster.appId === appId,
    R.sort(
      (a: FeaturedCluster, b: FeaturedCluster) =>
        a.position > b.position ? 1 : -1,
      R.values(featuredClusters.byId)
    )
  );
}



// WEBPACK FOOTER //
// ./src/store/Entities/FeaturedCluster/featured-cluster.reducer.ts